<template>
  <div
    class="d-flex align-center justify-center"
    :class="!isMobile() ? 'main-container-w-background' : 'main-container'"
  >
    <v-card
      v-if="loading"
      class="pa-5"
      style="border-radius: 10px"
      elevation="24"
    >
      <div class="d-flex align-center mt-4">
        <h1>Loading Account ...</h1>
        <v-spacer />
        <v-progress-circular
          :active="true"
          :indeterminate="true"
          size="25"
          width="2"
        />
      </div>
    </v-card>
    <v-card
      v-else-if="isTokenValid && isTokenValid.data && isTokenValid.data.message"
      :elevation="isMobile() ? 0 : 24"
      :class="!isMobile() ? 'pa-5 login-card' : ''"
      :style="!isMobile() ? 'width: 35%' : ''"
    >
      <div
        class="d-flex align-center justify-center"
        v-if="isTokenValid.data.message != 'Invalid Session Error'"
      >
        <v-form
          class="login-form"
          @submit.prevent="signupSalesRep"
          v-model="valid"
          ref="form"
        >
          <v-card-text class="ma-0 pa-0 mb-3">
            <v-text-field
              prepend-inner-icon="person"
              name="login"
              type="text"
              :value="isTokenValid.data.message"
              placeholder="Email..."
              data-testid="login"
              variant="solo"
              rounded
              readonly
            >
            </v-text-field>
            <v-text-field
              name="firstname"
              type="text"
              v-model="firstname"
              placeholder="Firstname"
              data-testid="firstname"
              :rules="[validateNotEmpty]"
              variant="solo"
              rounded
            >
            </v-text-field>
            <v-text-field
              name="lastname"
              type="text"
              v-model="lastname"
              placeholder="Lastname"
              data-testid="lastname"
              :rules="[validateNotEmpty]"
              variant="solo"
              rounded
            >
            </v-text-field>
            <v-text-field
              id="newPassword"
              prepend-inner-icon="lock"
              name="password"
              type="password"
              v-model="newPassword"
              :rules="passwordRules"
              placeholder="Password"
              data-testid="new-password"
              variant="solo"
              rounded
            >
            </v-text-field>
            <v-text-field
              id="confirmPassword"
              prepend-inner-icon="lock"
              name="password"
              type="password"
              v-model="confirmPassword"
              :rules="passwordRules"
              placeholder="Confirm Password"
              data-testid="confirm-password"
              variant="solo"
              rounded
            >
            </v-text-field>
            <div v-if="newPassword != confirmPassword">
              Passwords do not match
            </div>
          </v-card-text>
          <div class="text-xs-start">
            <v-btn
              block
              color="primary"
              type="submit"
              :loading="loading"
              :disabled="
                !firstname ||
                !lastname ||
                !passwordAcceptable ||
                !confirmPassword ||
                newPassword !== confirmPassword ||
                loading
              "
              data-testid="submit"
              class="btn-login"
              >Sign Up
            </v-btn>
          </div>
        </v-form>
      </div>
      <div v-else class="d-flex align-center justify-center">
        <h1>
          {{ isTokenValid.data.message }}
        </h1>
      </div>
    </v-card>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import store from "@/store";
import rules from "@/mixins/rules";
import helpers from "@/mixins/helpers";
// The shared account-password policy (twin of api/src/password_policy.py).
import { passwordRules, validatePassword } from "@/utils/passwordPolicy";
import { postLoginRedirect } from "@/router/postLoginRedirect";
import { useAccountStore } from "@/stores/account";

class User {
  constructor(data) {
    this.account_id = data.account_id;
    this.token = data.token;
    this.token_type = data.token_type;
  }
}
export default {
  mixins: [rules, helpers],

  data() {
    return {
      newPassword: "",
      confirmPassword: "",
      valid: false,
      passwordRules,
      isTokenValid: null,
      firstname: "",
      lastname: "",
      loading: false,
    };
  },

  computed: {
    // The new password satisfies the shared account-password policy.
    passwordAcceptable() {
      return validatePassword(this.newPassword) === true;
    },
  },

  async mounted() {
    this.loading = true;
    if (this.$route.params.id) {
      let resp = await this.$axios.get(
        `/auth/checkSignUpToken/${this.$route.params.id}`
      );
      if (resp.data.token) {
        // login automatically and redirect to the Onboarding Screen page
        // clear old account information in localStorage
        await this.$store.dispatch("logout");

        // save the token so we can make the next request to get the account's data
        const cleanToken = String(resp.data.token).replace(/^["']+|["']+$/g, '');
        localStorage.setItem("token", cleanToken);
        localStorage.setItem("userId", resp.data.account_id);
        
        // CRITICAL: Verify localStorage was actually written before proceeding
        const verifyToken = localStorage.getItem("token");
        if (verifyToken !== cleanToken) throw new Error('Token storage failed');
        
        let account = await this.$store.dispatch("jv/get", [
          `accounts/${resp.data.account_id}`,
          { params: { include: "role" } },
        ]);
        // /auth/checkSignUpToken returns only {token, account_id} -- it
        // carries NO capabilities, so we fetch them from /auth/me (the
        // canonical rehydration source; the axios interceptor sends the
        // bearer token we just stored). Without this, the Pinia
        // useAccountStore stays unhydrated: postLoginRedirect /
        // dispatchNeutralEntry in the requireToken guard then see empty
        // capabilities and bounce the new user to /login even though
        // store.state.account is populated (the #1931 await fix).
        const meResp = await this.$axios.get("/auth/me");
        // save the new account information in localStorage
        // Await login so store.state.account is repopulated BEFORE we
        // navigate; the requireToken guard reads store.state.account for
        // its in-session fast-path, and an un-awaited login raced the push
        // and bounced new users to /login. Pass authResponse (the /auth/me
        // payload) so the PR-11 login shape ALSO hydrates Pinia
        // capabilities.
        // GH #2941 defect 2: credentials were just set, so clear any
        // anonymous-storefront marker. See
        // router/anonymousStorefrontSession.js.
        await this.$store.dispatch("login", {
          account,
          authResponse: meResp.data,
          credentialed: true,
        });
        this.$router.push(this.postSignupRoute(account));
      } else {
        this.isTokenValid = resp;
      }
    }
    this.loading = false;
  },

  methods: {
    async signupSalesRep() {
      // Enter in a field submits without the (disabled) button, so the policy
      // is re-checked here before anything is posted.
      if (this.passwordAcceptable && this.$refs.form.validate()) {
        let email = this.isTokenValid.data.message;
        let resp;
        try {
          resp = await this.$axios.post("/auth/signUpSalesRep", {
            username: email,
            firstname: this.firstname,
            lastname: this.lastname,
            email: email,
            password: this.confirmPassword,
            reset_code: this.$route.params.id,
          });
        } catch (err) {
          // A refusal (for example the server's account-password policy)
          // has already been toasted by the response interceptor. Stay on
          // the form: the server does not consume the invitation for a
          // rejected password.
          return;
        }
        // NOTE: A Google Analytics gtag('purchase') event (fed by a
        // hardcoded /auth/getSubscriptionInfo/1 fetch) used to fire here on
        // an "AddSalesRep" response. It modeled the retired per-seat
        // billing model (seats are $0 now) and crashed builder signup: the
        // backend endpoint 500s, the catch did not return, and
        // `result.data` then dereferenced undefined -> TypeError, aborting
        // the whole signup. Removed as obsolete. Builder signup now
        // proceeds straight to login + account load.

        try {
          resp = await this.$axios.post("/auth/login", {
            login: email,
            password: this.confirmPassword,
          });
        } catch (err) {
          console.log(describeError(err));
          this.$store.dispatch("raiseError", err);
          return;
        }
        // clear old account information in localStorage
        await this.$store.dispatch("logout");

        // save the token so we can make the next request to get the account's data
        const cleanToken = String(resp.data.token).replace(/^["']+|["']+$/g, '');
        localStorage.setItem("token", cleanToken);
        localStorage.setItem("userId", resp.data.account_id);
        
        // CRITICAL: Verify localStorage was actually written before proceeding
        const verifyToken = localStorage.getItem("token");
        if (verifyToken !== cleanToken) throw new Error('Token storage failed');
        
        let account = await this.$store.dispatch("jv/get", [
          `accounts/${resp.data.account_id}`,
          { params: { include: "role" } },
        ]);
        // save the new account information in localStorage
        // Await login so store.state.account is repopulated BEFORE we
        // navigate; the requireToken guard reads store.state.account for
        // its in-session fast-path, and an un-awaited login raced the push
        // and bounced new users to /login. `resp` here is the /auth/login
        // response, which carries `capabilities` -- pass it as authResponse
        // so the PR-11 login shape ALSO hydrates the Pinia useAccountStore
        // (mirrors TheLoginPage.vue). Without it, postLoginRedirect /
        // dispatchNeutralEntry see empty caps and bounce to /login.
        // GH #2941 defect 2: credentialed sign-in -- clear any anonymous
        // marker. See router/anonymousStorefrontSession.js.
        await this.$store.dispatch("login", {
          account,
          authResponse: resp.data,
          credentialed: true,
        });
        this.$router.push(this.postSignupRoute(account));
      }
    },

    // Post-activation landing page selection -- a ROUTING decision, not a
    // permission gate (the destination route's own guard enforces caps).
    //
    // The /new-user/:token magic-link landing is SHARED by several audiences
    // whose CreateTeammate rows differ by distributor_id:
    //   - the distributor OWNER activating their own org (org setup: Stripe /
    //     Suppliers / Nexus) -> /distributor/setup.
    //   - an invited distributor TEAM MEMBER (e.g. sales_executive) joining an
    //     EXISTING org they did NOT set up -> /distributor/stores (#1967).
    //   - a store-editor-provisioned CLIENT ADMIN / STORE MANAGER (card
    //     8wGst90i) -> /portal. These roles hold portal.access (NOT
    //     distributor.access), so a distributor surface would 403 them; the
    //     portal is their home.
    // Both audiences pass through both push sites (the auto-login branch AND
    // the form-submit branch -- signUpSalesRep also completes a
    // pre-provisioned owner), so the landing is resolved AFTER the `login`
    // dispatch hydrates capabilities, not on which code path ran.
    //
    // CAPABILITY-DRIVEN (design 2026-07-09 Section 7.2). The owner setup
    // landing is the ONE special-case postLoginRedirect cannot express (it
    // has no /distributor/setup destination), so owner is handled first;
    // everyone else defers to the shared postLoginRedirect capability map
    // (portal.access -> /portal; distributor.access-without-reporting ->
    // /distributor/stores). Capabilities come from the Pinia useAccountStore,
    // which BOTH push sites hydrate (via the `login` { account, authResponse }
    // dispatch) before this runs. Owner detection still keys on the role SLUG
    // (owner is the only role that needs the non-capability setup landing);
    // role ids drift across DBs so never key on the id.
    //
    // NOTE: the owner setup/onboarding rework is tracked separately (Boulders
    // card h5OGeryk); when it lands it should reconcile the owner branch here
    // (ideally folding /distributor/setup into the capability map).
    postSignupRoute(account) {
      const slug = account && account.role && account.role.slug;
      if (slug === "owner") {
        return "/distributor/setup";
      }
      // Resolve capabilities from the hydrated account store; postLoginRedirect
      // maps portal.access -> /portal and distributor.access(-without-reporting)
      // -> /distributor/stores. An empty/unhydrated caps list falls through to
      // /distributor/stores (the prior non-owner default) rather than /login,
      // preserving the #1967 team-member landing when caps are unavailable.
      // The Pinia read is wrapped defensively: if the account store is not
      // active (an edge path, or a unit test that does not install Pinia) we
      // fall back to the safe non-owner default rather than throwing out of a
      // routing decision.
      let capabilities = [];
      try {
        capabilities = useAccountStore().capabilities || [];
      } catch (_e) {
        capabilities = [];
      }
      const destination = postLoginRedirect({ capabilities });
      return destination === "/login" ? "/distributor/stores" : destination;
    },
  },
};
</script>

<style scoped>
.login {
  z-index: 100;
}

.background {
  position: absolute;
  width: 100%;
  height: 100%;
}

.background-pos {
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

@media only screen and (max-width: 767px) {
  .background-pos {
    display: none;
  }
}

.background-img {
  position: absolute;
  left: 0;
  height: 100vh;
}

.btn-login {
  height: 48px !important;
  border-radius: 50px !important;
}
.login {
  display: flex;
  justify-content: center;
}
.login-form {
  width: 90%;
}

.main-container-w-background {
  height: 100vh;
  background-color: #f5f8fa;
}

.main-container {
  height: 100%;
  width: 100%;
}

.login-card {
  border-radius: 10px;
}

.tf-logo {
  image-rendering: crisp-edges;
}

.btn-forgot {
  width: 100%;
  margin-left: 0;
}
</style>
